import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import api, { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      if (r.data?.role !== "admin") {
        setError("This area is restricted to admin accounts.");
        return;
      }
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#FFFFFF] border border-[#E4E4E7] px-4 py-3.5 text-sm text-[#09090B] placeholder:text-[#A1A1AA] focus:outline-none focus:border-[#6D28D9] focus:ring-1 focus:ring-[#6D28D9]/40 transition-colors duration-300";

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#FBFBFC] text-[#09090B] flex items-center justify-center px-6">
      <div className="noise-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <div className="flex items-center gap-3 mb-10 justify-center">
          <Logo className="w-9 h-9" />
          <span className="font-display font-bold tracking-[0.18em] text-sm uppercase">Aetheris Labs — Admin</span>
        </div>
        <form onSubmit={submit} data-testid="admin-login-form" className="border border-[#E4E4E7] bg-[#FFFFFF] p-8 space-y-5">
          <input
            type="email"
            required
            data-testid="admin-email-input"
            placeholder="Admin email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
          />
          <input
            type="password"
            required
            data-testid="admin-password-input"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputCls}
          />
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-400 font-mono-clinical">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full px-8 py-4 bg-[#6D28D9] text-white font-mono-clinical text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#5B21B6] transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Sign In
          </button>
        </form>
        <a href="/" data-testid="admin-login-back" className="block text-center mt-6 font-mono-clinical text-[11px] tracking-[0.2em] uppercase text-[#A1A1AA] hover:text-[#6D28D9] transition-colors">
          Back to site
        </a>
      </motion.div>
    </div>
  );
}
